// src/pages/dashboard/Reviews.js
// Route: /dashboard/reviews
// Practitioner view: DF reviews (manage + respond) + Google reviews (import + display).

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Star, Flag, MessageSquare, Check, X, ChevronDown, AlertCircle, RefreshCw, Search, ExternalLink,
} from 'lucide-react';
import { auth, db } from '../../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { useScrollToHash } from '../../utils/useScrollToHash';
import { doc, onSnapshot } from 'firebase/firestore';
import { subscribeToReviews, respondToReview, flagReview } from '../../services/review-service';
import {
  searchGooglePlaces, connectGoogleBusiness, refreshGoogleReviews,
  disconnectGoogleBusiness, subscribeToGoogleReviews,
} from '../../services/google-reviews-service';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (ts) => {
  if (!ts) return '';
  const d = ts?.toDate ? ts.toDate() : new Date(ts._seconds ? ts._seconds * 1000 : ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const StarDisplay = ({ rating, size = 16 }) => (
  <div style={{ display: 'flex', gap: '2px' }}>
    {[1, 2, 3, 4, 5].map(s => (
      <Star key={s} size={size} fill={s <= rating ? '#F59E0B' : 'none'} color={s <= rating ? '#F59E0B' : '#D1D5DB'} strokeWidth={1.5} />
    ))}
  </div>
);

const STATUS_COLORS = {
  published: { bg: '#ECFDF5', text: '#065F46', label: 'Published' },
  pending:   { bg: '#FFF7ED', text: '#92400E', label: 'Pending' },
  flagged:   { bg: '#FEF2F2', text: '#991B1B', label: 'Flagged' },
  removed:   { bg: '#F3F4F6', text: '#6B7280', label: 'Removed' },
};

// Google "G" logo (SVG inline — required for attribution per Google ToS)
const GoogleGLogo = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

// ─── Respond Modal ────────────────────────────────────────────────────────────
const RespondModal = ({ review, onClose, onSubmit }) => {
  const [text, setText] = useState(review.practitionerResponse || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) { setError('Please write a response.'); return; }
    setSaving(true);
    try {
      await onSubmit(review.id, text.trim());
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save response.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Respond Publicly</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="bg-gray-50 rounded-xl p-4 mb-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <StarDisplay rating={review.rating} size={14} />
            <span className="text-sm text-gray-500">· {review.isAnonymous ? 'Anonymous' : (review.displayName || 'Client')}</span>
          </div>
          <p className="text-sm text-gray-700 line-clamp-3">{review.body}</p>
        </div>
        <form onSubmit={handleSubmit}>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Your Response</label>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={5}
            placeholder="Thank them for their review and share any additional thoughts…"
            className="w-full border border-gray-200 rounded-xl p-3 text-sm text-gray-900 resize-none focus:outline-none focus:border-purple-500 transition-colors"
            style={{ fontFamily: 'inherit' }} />
          <p className="text-xs text-gray-400 mt-1 mb-4">Your response will appear publicly below the review.</p>
          {error && <p className="text-sm text-red-600 mb-3 flex items-center gap-2"><AlertCircle size={14} /> {error}</p>}
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 py-2.5 bg-purple-700 text-white rounded-xl text-sm font-semibold hover:bg-purple-800 disabled:opacity-60">
              {saving ? 'Saving…' : 'Publish Response'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Flag Modal (enhanced — reason categories + explanation) ──────────────────
const FLAG_REASONS = [
  { value: 'inappropriate_content', label: 'Inappropriate or offensive content' },
  { value: 'not_related_to_services', label: 'Not related to my services' },
  { value: 'factually_inaccurate', label: 'Factually inaccurate' },
  { value: 'suspected_fake', label: 'Suspected fake review' },
  { value: 'spam_promotional', label: 'Spam or promotional content' },
  { value: 'other', label: 'Other' },
];

const FlagModal = ({ review, onClose, onSubmit }) => {
  const [reason, setReason] = useState('');
  const [explanation, setExplanation] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = reason && (reason !== 'other' || explanation.trim());

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError('');
    try {
      await onSubmit(review.id, reason, explanation.trim() || null);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to flag review. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} style={{ fontFamily: "'Montserrat', sans-serif" }}>
        <div className="flex items-center justify-between mb-4">
          <h3 style={{ fontSize: '17px', fontWeight: 700, color: '#111827' }}>Flag this review</h3>
          <span
            role="button" tabIndex={0}
            onClick={onClose}
            onKeyDown={(e) => e.key === 'Enter' && onClose()}
            style={{ fontSize: '20px', color: '#9CA3AF', cursor: 'pointer', userSelect: 'none', lineHeight: 1 }}
          >&times;</span>
        </div>

        <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '6px', lineHeight: 1.5 }}>
          Please tell us why this review should be reviewed by our team.
        </p>
        <p style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '16px', lineHeight: 1.5, fontStyle: 'italic' }}>
          Note: A negative review is not an unfair review. We only remove reviews that violate our guidelines.
        </p>

        {/* Reason radio buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
          {FLAG_REASONS.map(({ value, label }) => (
            <label key={value} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#374151' }}>
              <input
                type="radio" name="flagReason" value={value}
                checked={reason === value}
                onChange={() => setReason(value)}
                style={{ accentColor: '#7c3aed' }}
              />
              {label}
            </label>
          ))}
        </div>

        {/* Explanation field */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#6B7280', marginBottom: '4px' }}>
            Explain briefly{reason === 'other' ? ' (required)' : ' (optional)'}:
          </label>
          <textarea
            value={explanation}
            onChange={e => setExplanation(e.target.value.slice(0, 500))}
            rows={3}
            placeholder="Provide additional context..."
            style={{
              width: '100%', padding: '10px 12px', border: '1.5px solid #D1D5DB',
              borderRadius: '8px', fontSize: '13px', fontFamily: "'Montserrat', sans-serif",
              resize: 'none', outline: 'none',
            }}
            onFocus={(e) => { e.target.style.borderColor = '#7c3aed'; }}
            onBlur={(e) => { e.target.style.borderColor = '#D1D5DB'; }}
          />
          <p style={{ fontSize: '11px', color: '#9CA3AF', textAlign: 'right', marginTop: '2px' }}>
            {explanation.length}/500
          </p>
        </div>

        {error && (
          <p style={{ fontSize: '12px', color: '#DC2626', marginBottom: '12px' }}>{error}</p>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          <span
            role="button" tabIndex={0}
            onClick={onClose}
            onKeyDown={(e) => e.key === 'Enter' && onClose()}
            style={{
              flex: 1, padding: '10px', border: '1.5px solid #D1D5DB', borderRadius: '10px',
              textAlign: 'center', fontSize: '13px', fontWeight: 600, color: '#374151',
              cursor: 'pointer', userSelect: 'none',
            }}
          >
            Cancel
          </span>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || saving}
            style={{
              flex: 1, padding: '10px', border: 'none', borderRadius: '10px',
              background: canSubmit && !saving ? '#DC2626' : '#E5E7EB',
              color: canSubmit && !saving ? 'white' : '#9CA3AF',
              fontSize: '13px', fontWeight: 600,
              cursor: canSubmit && !saving ? 'pointer' : 'not-allowed',
              fontFamily: "'Montserrat', sans-serif",
            }}
          >
            {saving ? 'Flagging...' : 'Submit flag'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── DF Review Card ───────────────────────────────────────────────────────────
const ReviewCard = ({ review, onRespond, onFlag }) => {
  const status = STATUS_COLORS[review.status] || STATUS_COLORS.pending;
  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <StarDisplay rating={review.rating} />
              <span className="text-sm font-semibold text-gray-900">
                {review.isAnonymous ? 'Anonymous' : (review.displayName || 'Client')}
              </span>
              <span className="text-xs text-gray-400">{formatDate(review.submittedAt)}</span>
              <span style={{ padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: status.bg, color: status.text }}>
                {status.label}
              </span>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">{review.body}</p>
            {review.practitionerResponse && (
              <div className="mt-3 bg-purple-50 border border-purple-100 rounded-xl p-3">
                <p className="text-xs font-semibold text-purple-700 mb-1">Your Response</p>
                <p className="text-sm text-gray-700">{review.practitionerResponse}</p>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            {review.status === 'published' && (
              <button onClick={() => onRespond(review)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors">
                <MessageSquare size={13} />
                {review.practitionerResponse ? 'Edit Reply' : 'Reply'}
              </button>
            )}
            {(review.flagged || review.status === 'flagged') ? (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                padding: '4px 10px', background: '#FEF3C7', color: '#92400E',
                borderRadius: '8px', fontSize: '11px', fontWeight: 600,
                fontFamily: "'Montserrat', sans-serif",
              }}>
                <Flag size={11} /> Flagged — under review
              </span>
            ) : review.status !== 'removed' && (
              <span
                role="button" tabIndex={0}
                onClick={() => onFlag(review)}
                onKeyDown={(e) => e.key === 'Enter' && onFlag(review)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  padding: '4px 10px', border: '1px solid #E5E7EB', borderRadius: '8px',
                  fontSize: '11px', fontWeight: 500, color: '#6B7280',
                  cursor: 'pointer', userSelect: 'none',
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                <Flag size={11} /> Flag
              </span>
            )}
          </div>
        </div>
        {review.serviceName && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-400">Service: <strong className="text-gray-600">{review.serviceName}</strong></p>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Google Review Card (read-only) ──────────────────────────────────────────
const GoogleReviewCard = ({ review }) => (
  <div className="bg-white border border-gray-200 rounded-2xl p-5">
    <div className="flex items-start gap-3 mb-3">
      {review.profilePhotoUrl ? (
        <img src={review.profilePhotoUrl} alt={review.authorName} className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
      ) : (
        <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 text-sm font-bold text-gray-500">
          {review.authorName?.[0] || 'G'}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-gray-900">{review.authorName || 'Google User'}</span>
          <span className="text-xs text-gray-400">{review.relativeTimeDescription || formatDate(review.time)}</span>
        </div>
        <StarDisplay rating={review.rating} size={14} />
      </div>
      <GoogleGLogo size={16} />
    </div>
    {review.text ? (
      <p className="text-sm text-gray-700 leading-relaxed">{review.text}</p>
    ) : (
      <p className="text-sm text-gray-400 italic">No written review</p>
    )}
  </div>
);

// ─── Rating Breakdown Bar ─────────────────────────────────────────────────────
const RatingBar = ({ label, count, total }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
      <span style={{ fontSize: '13px', color: '#6B7280', width: '14px', textAlign: 'right' }}>{label}</span>
      <Star size={12} fill="#F59E0B" color="#F59E0B" />
      <div style={{ flex: 1, height: '8px', background: '#F3F4F6', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: '#F59E0B', borderRadius: '4px', transition: 'width 0.5s ease' }} />
      </div>
      <span style={{ fontSize: '12px', color: '#9CA3AF', width: '24px' }}>{count}</span>
    </div>
  );
};

// ─── Google Connect Panel ─────────────────────────────────────────────────────
const GoogleConnectPanel = ({ userId, googleBusiness, onConnected, onDisconnected, onRefreshed }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setSearching(true);
    setError('');
    setSearchResults([]);
    try {
      const results = await searchGooglePlaces(searchQuery.trim());
      setSearchResults(results);
      if (results.length === 0) setError('No businesses found. Try a more specific search.');
    } catch (err) {
      setError(err.message?.includes('not configured')
        ? 'Google Places API is not yet configured. See GOOGLE_REVIEWS_SETUP.md for setup instructions.'
        : (err.message || 'Search failed. Please try again.'));
    } finally {
      setSearching(false);
    }
  };

  const handleConnect = async (place) => {
    setConnecting(true);
    setError('');
    try {
      const result = await connectGoogleBusiness(place.placeId, place.name);
      setSuccessMsg(`Connected! ${result.reviewCount} review${result.reviewCount !== 1 ? 's' : ''} imported.`);
      setSearchResults([]);
      setSearchQuery('');
      onConnected();
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed to connect. Please try again.');
    } finally {
      setConnecting(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError('');
    try {
      const result = await refreshGoogleReviews();
      setSuccessMsg(`Reviews refreshed — ${result.reviewCount} review${result.reviewCount !== 1 ? 's' : ''} imported.`);
      onRefreshed();
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      setError(err.message || 'Refresh failed.');
    } finally {
      setRefreshing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Remove your Google Business connection and all imported reviews?')) return;
    setDisconnecting(true);
    try {
      await disconnectGoogleBusiness();
      onDisconnected();
    } catch (err) {
      setError(err.message || 'Failed to disconnect.');
    } finally {
      setDisconnecting(false);
    }
  };

  // Connected state
  if (googleBusiness?.connected) {
    const lastFetched = googleBusiness.lastFetched?.toDate
      ? googleBusiness.lastFetched.toDate()
      : googleBusiness.lastFetched?._seconds
        ? new Date(googleBusiness.lastFetched._seconds * 1000)
        : null;

    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white border border-gray-200 rounded-xl flex items-center justify-center flex-shrink-0">
              <GoogleGLogo size={22} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">{googleBusiness.businessName}</span>
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Connected</span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                {googleBusiness.reviewCount || 0} review{googleBusiness.reviewCount !== 1 ? 's' : ''} imported
                {googleBusiness.averageRating && ` · ${googleBusiness.averageRating.toFixed(1)} ★ on Google`}
                {lastFetched && ` · Last updated ${lastFetched.toLocaleDateString()}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
            <span
              role="button"
              tabIndex={0}
              onClick={handleDisconnect}
              onKeyDown={e => { if (e.key === 'Enter') handleDisconnect(); }}
              className="text-xs text-red-500 hover:text-red-700 cursor-pointer transition-colors"
              style={{ userSelect: 'none' }}
            >
              {disconnecting ? 'Disconnecting…' : 'Disconnect'}
            </span>
          </div>
        </div>

        {successMsg && (
          <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-2.5 flex items-center gap-2">
            <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
            <span className="text-xs text-green-700 font-medium">{successMsg}</span>
          </div>
        )}
        {error && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-2.5 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <span className="text-xs text-red-600">{error}</span>
          </div>
        )}
      </div>
    );
  }

  // Connect state — search + results
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-white border border-gray-200 rounded-xl flex items-center justify-center flex-shrink-0">
          <GoogleGLogo size={22} />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Connect Google Reviews</h3>
          <p className="text-xs text-gray-500">Import your existing Google Business reviews to display alongside DF reviews</p>
        </div>
      </div>

      <form onSubmit={handleSearch} className="flex gap-2 mb-3">
        <input
          type="text"
          value={searchQuery}
          onChange={e => { setSearchQuery(e.target.value); setError(''); }}
          placeholder="Search your business name…"
          className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-purple-400 transition-colors"
          style={{ fontFamily: 'inherit' }}
        />
        <button
          type="submit"
          disabled={searching || !searchQuery.trim()}
          className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-700 disabled:opacity-50 transition-colors"
        >
          <Search size={14} className={searching ? 'animate-pulse' : ''} />
          {searching ? 'Searching…' : 'Search'}
        </button>
      </form>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 mb-3 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <span className="text-xs text-red-600">{error}</span>
        </div>
      )}

      {searchResults.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Select your business</p>
          {searchResults.map(place => (
            <div key={place.placeId} className="flex items-center justify-between gap-3 p-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{place.name}</p>
                <p className="text-xs text-gray-500 truncate">{place.address}</p>
                {place.rating && (
                  <p className="text-xs text-amber-600 font-medium mt-0.5">
                    {place.rating} ★ · {place.totalRatings?.toLocaleString()} reviews on Google
                  </p>
                )}
              </div>
              <button
                onClick={() => handleConnect(place)}
                disabled={connecting}
                className="px-3 py-1.5 bg-purple-700 text-white text-xs font-semibold rounded-lg hover:bg-purple-800 disabled:opacity-60 transition-colors flex-shrink-0"
              >
                {connecting ? 'Connecting…' : 'Select'}
              </button>
            </div>
          ))}
        </div>
      )}

      {successMsg && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-2.5 flex items-center gap-2">
          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
          <span className="text-xs text-green-700 font-medium">{successMsg}</span>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Reviews() {
  useScrollToHash();
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('df'); // 'df' | 'google'

  // DF Reviews state
  const [reviews, setReviews] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [respondTarget, setRespondTarget] = useState(null);
  const [flagTarget, setFlagTarget] = useState(null);
  const [actionMsg, setActionMsg] = useState('');

  // Google Reviews state
  const [googleReviews, setGoogleReviews] = useState([]);
  const [googleBusiness, setGoogleBusiness] = useState(null);
  const [googleRefreshKey, setGoogleRefreshKey] = useState(0);

  // Auth — safe pattern: if Firebase already has the current user (SPA navigation),
  // use it immediately. Otherwise wait for onAuthStateChanged.
  // The 3s timeout covers the truly-unauthenticated case.
  useEffect(() => {
    if (auth?.currentUser) {
      setUserId(auth.currentUser.uid);
      setLoading(false);
      return;
    }
    let done = false;
    const timeout = setTimeout(() => { if (!done) { done = true; setLoading(false); } }, 3000);
    const unsub = onAuthStateChanged(auth, (user) => {
      if (done) return;
      if (!user) return;
      done = true;
      clearTimeout(timeout);
      setUserId(user.uid);
      setLoading(false);
    });
    return () => { done = true; clearTimeout(timeout); unsub(); };
  }, []);

  // Subscribe to DF reviews
  useEffect(() => {
    if (!userId) return;
    return subscribeToReviews(userId, setReviews);
  }, [userId]);

  // Subscribe to Google reviews
  useEffect(() => {
    if (!userId) return;
    return subscribeToGoogleReviews(userId, setGoogleReviews);
  }, [userId, googleRefreshKey]); // eslint-disable-line

  // Subscribe to practitioner doc for googleBusiness status
  useEffect(() => {
    if (!userId || !db) return;
    return onSnapshot(doc(db, 'practitioners', userId), (snap) => {
      const data = snap.data() || {};
      setGoogleBusiness(data.googleBusiness || null);
    }, (err) => {
      console.error('practitioners subscription error:', err.message);
    });
  }, [userId]);

  const handleRespond = async (reviewId, response) => {
    await respondToReview(reviewId, response);
    setActionMsg('Response published successfully.');
    setTimeout(() => setActionMsg(''), 3000);
  };

  const handleFlag = async (reviewId, reason, explanation) => {
    await flagReview(reviewId, reason, explanation);
    setActionMsg('Review flagged for moderation.');
    setTimeout(() => setActionMsg(''), 3000);
  };

  // DF stats
  const published = reviews.filter(r => r.status === 'published');
  const totalReviews = published.length;
  const averageRating = totalReviews > 0
    ? (published.reduce((sum, r) => sum + r.rating, 0) / totalReviews)
    : 0;
  const breakdown = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  published.forEach(r => { if (breakdown[r.rating] !== undefined) breakdown[r.rating]++; });

  const filteredReviews = filterStatus === 'all'
    ? reviews
    : reviews.filter(r => r.status === filterStatus);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-500">Loading reviews…</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
          <ChevronDown className="w-5 h-5 text-gray-500 rotate-90" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reviews</h1>
          <p className="text-sm text-gray-500 mt-0.5">Client feedback and ratings</p>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-2xl mb-6 w-fit">
        <button
          onClick={() => setActiveTab('df')}
          className={`px-5 py-2 rounded-xl text-sm font-semibold transition-all ${
            activeTab === 'df' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          DF Reviews {reviews.length > 0 && `(${reviews.length})`}
        </button>
        <button
          onClick={() => setActiveTab('google')}
          className={`flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold transition-all ${
            activeTab === 'google' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <GoogleGLogo size={14} />
          Google {googleReviews.length > 0 && `(${googleReviews.length})`}
        </button>
      </div>

      {/* ── DF Reviews Tab ─────────────────────────────────────────────────────── */}
      {activeTab === 'df' && (
        <>
          {actionMsg && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-6 flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-700 font-medium">{actionMsg}</span>
            </div>
          )}

          {/* Stats Card */}
          {totalReviews > 0 && (
            <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6">
              <div className="flex items-start gap-8">
                <div className="text-center">
                  <div className="text-5xl font-bold text-gray-900 mb-1">{averageRating.toFixed(1)}</div>
                  <StarDisplay rating={Math.round(averageRating)} size={18} />
                  <p className="text-sm text-gray-500 mt-2">{totalReviews} review{totalReviews !== 1 ? 's' : ''}</p>
                </div>
                <div className="flex-1 min-w-0">
                  {[5, 4, 3, 2, 1].map(n => (
                    <RatingBar key={n} label={n} count={breakdown[n]} total={totalReviews} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Status filter tabs */}
          <div className="flex gap-2 mb-5 flex-wrap">
            {['all', 'published', 'pending', 'flagged'].map(s => {
              const count = s === 'all' ? reviews.length : reviews.filter(r => r.status === s).length;
              return (
                <button key={s} onClick={() => setFilterStatus(s)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                    filterStatus === s ? 'bg-purple-700 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}>
                  {s.charAt(0).toUpperCase() + s.slice(1)} {count > 0 && `(${count})`}
                </button>
              );
            })}
          </div>

          {/* Review list */}
          {filteredReviews.length === 0 ? (
            <div className="text-center py-16 bg-white border border-gray-200 rounded-2xl">
              <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">
                {filterStatus === 'all' ? 'No DF reviews yet' : `No ${filterStatus} reviews`}
              </h3>
              <p className="text-sm text-gray-500 max-w-xs mx-auto">
                {filterStatus === 'all'
                  ? 'Review requests are sent automatically 24–48 hours after completed sessions.'
                  : `Reviews with "${filterStatus}" status will appear here.`}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredReviews.map(review => (
                <ReviewCard key={review.id} review={review} onRespond={setRespondTarget} onFlag={setFlagTarget} />
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Google Reviews Tab ─────────────────────────────────────────────────── */}
      {activeTab === 'google' && (
        <>
          {/* Connection panel (search or connected status) */}
          <div id="google-business">
            <GoogleConnectPanel
              userId={userId}
              googleBusiness={googleBusiness}
              onConnected={() => setGoogleRefreshKey(k => k + 1)}
              onDisconnected={() => setGoogleRefreshKey(k => k + 1)}
              onRefreshed={() => setGoogleRefreshKey(k => k + 1)}
            />
          </div>

          {/* Google reviews list */}
          {googleReviews.length > 0 ? (
            <>
              {/* Attribution — required by Google ToS */}
              <div className="flex items-center gap-2 mb-4">
                <GoogleGLogo size={14} />
                <span className="text-xs text-gray-500">Reviews from Google · Read-only · Showing up to 5 most recent</span>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(googleBusiness?.businessName || '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
                >
                  View on Google <ExternalLink size={11} />
                </a>
              </div>
              <div className="space-y-4">
                {googleReviews.map(review => (
                  <GoogleReviewCard key={review.id} review={review} />
                ))}
              </div>
            </>
          ) : googleBusiness?.connected ? (
            <div className="text-center py-10 bg-white border border-gray-200 rounded-2xl">
              <p className="text-sm text-gray-500">No reviews imported yet. Try refreshing.</p>
            </div>
          ) : null}
        </>
      )}

      {/* Modals */}
      {respondTarget && (
        <RespondModal review={respondTarget} onClose={() => setRespondTarget(null)} onSubmit={handleRespond} />
      )}
      {flagTarget && (
        <FlagModal review={flagTarget} onClose={() => setFlagTarget(null)} onSubmit={handleFlag} />
      )}
    </div>
  );
}
